import type {
  EngineToStudioMessage,
  ProjectManifest,
  StudioToEngineMessage,
} from "@pix/shared";

/**
 * Studio に埋め込まれた場合の通信ブリッジ。
 * URL パラメータ `?mode=embed` で有効化される。
 */

export interface EmbedProject {
  manifest: ProjectManifest;
  scripts: Record<string, string>;
  /** path → dataURL。manifest の file パスをキーに優先解決される */
  assets: Record<string, string>;
  assetBaseUrl: string;
}

export class EmbedBridge {
  private listeners: Array<(msg: StudioToEngineMessage) => void> = [];
  private currentProject: EmbedProject | null = null;

  static isEmbedMode(): boolean {
    const params = new URLSearchParams(window.location.search);
    return params.get("mode") === "embed";
  }

  constructor() {
    window.addEventListener("message", (e: MessageEvent) => {
      const msg = e.data as StudioToEngineMessage;
      if (typeof msg !== "object" || msg === null || !("type" in msg)) return;
      if (msg.type === "loadProject") {
        this.currentProject = {
          manifest: msg.manifest,
          scripts: msg.scripts,
          assets: msg.assets ?? {},
          assetBaseUrl: msg.assetBaseUrl ?? "./sample",
        };
      }
      this.listeners.forEach((cb) => cb(msg));
    });
  }

  onMessage(cb: (msg: StudioToEngineMessage) => void): () => void {
    this.listeners.push(cb);
    return () => {
      this.listeners = this.listeners.filter((c) => c !== cb);
    };
  }

  send(msg: EngineToStudioMessage): void {
    if (window.parent && window.parent !== window) {
      window.parent.postMessage(msg, "*");
    }
  }

  getProject(): EmbedProject | null {
    return this.currentProject;
  }

  /** Studio が project を送ってくるまで待つ */
  waitForProject(timeoutMs = 30000): Promise<EmbedProject> {
    return new Promise((resolve, reject) => {
      if (this.currentProject) {
        resolve(this.currentProject);
        return;
      }
      const timer = window.setTimeout(() => {
        cleanup();
        reject(new Error("Studio からプロジェクトを受信できませんでした"));
      }, timeoutMs);
      const unsub = this.onMessage((msg) => {
        if (msg.type === "loadProject" && this.currentProject) {
          cleanup();
          resolve(this.currentProject);
        }
      });
      const cleanup = () => {
        window.clearTimeout(timer);
        unsub();
      };
    });
  }

  /** Studio が start を送ってくるまで待つ */
  waitForStart(): Promise<void> {
    return new Promise((resolve) => {
      const unsub = this.onMessage((msg) => {
        if (msg.type === "start") {
          unsub();
          resolve();
        }
      });
    });
  }
}
